import { cutoutRing, edgeFrame } from "@/lib/planGeometry";

// Moving / rotating / flipping / snapping pieces on the 3D floor. Positions live in
// quote.design.arrange[areaId][pieceId] = { x, y, rot, flip_h, flip_v, attach, stand }
// and are layered over the drawing until "Apply to drawing" writes them back.

export const SNAP_MODES = [
  ["grid", "Grid + edges"],
  ["edges", "Edges only"],
  ["off", "Snap off"],
];
export const SNAP_IN = 3;
const ANG_TOL = 8;
const GRID_IN = 1;

const rad = (d) => (d * Math.PI) / 180;
const deg = (r) => (r * 180) / Math.PI;
export const norm180 = (a) => ((((a + 180) % 360) + 360) % 360) - 180;
export const norm360 = (a) => ((a % 360) + 360) % 360;
export const round2 = (v) => Math.round(v * 100) / 100;
export const sideLetter = (i) => String.fromCharCode(65 + ((Number(i) || 0) % 26));

export const pieceCenter = (p) => [
  (p.x || 0) + (Number(p.length_in) || 0) / 2,
  (p.y || 0) + (Number(p.width_in) || 0) / 2,
];

// Plan point (x right, y down) -> world X/Z, same transform the 3D piece groups use.
export const planToWorld = (p, [lx, ly]) => {
  const [cx, cz] = pieceCenter(p);
  const th = (-(Number(p.rot) || 0) * Math.PI) / 180;
  const vx = lx - (Number(p.length_in) || 0) / 2;
  const vz = ly - (Number(p.width_in) || 0) / 2;
  return [
    vx * Math.cos(th) + vz * Math.sin(th) + cx,
    -vx * Math.sin(th) + vz * Math.cos(th) + cz,
  ];
};

export const dirToWorld = (p, [dx, dy]) => {
  const th = (-(Number(p.rot) || 0) * Math.PI) / 180;
  return [dx * Math.cos(th) + dy * Math.sin(th), -dx * Math.sin(th) + dy * Math.cos(th)];
};

// Every side of a piece in world space with its outward normal and tangent.
export const worldEdges = (p) => {
  const pts = p.points || [];
  return pts.map((a, i) => {
    const f = edgeFrame(pts, i);
    const A = planToWorld(p, a);
    const B = planToWorld(p, pts[(i + 1) % pts.length]);
    const [nx, nz] = dirToWorld(p, [f.nx, f.ny]);
    return {
      i,
      A,
      B,
      len: f.len,
      nx,
      nz,
      tx: (B[0] - A[0]) / f.len,
      tz: (B[1] - A[1]) / f.len,
    };
  });
};

const projT = (e, P) => (P[0] - e.A[0]) * e.tx + (P[1] - e.A[1]) * e.tz;
const projN = (e, P) => (P[0] - e.A[0]) * e.nx + (P[1] - e.A[1]) * e.nz;
const mid = (e) => [(e.A[0] + e.B[0]) / 2, (e.A[1] + e.B[1]) / 2];

// The closest pair of sides that face each other within the angle + gap tolerance.
const facingPair = (mine, statics, tol) => {
  let best = null;
  const cosTol = -Math.cos(rad(ANG_TOL));
  mine.forEach((e) =>
    statics.forEach((s) => {
      if (e.nx * s.nx + e.nz * s.nz > cosTol) return;
      const sa = projT(e, s.A);
      const sb = projT(e, s.B);
      const overlap =
        Math.min(e.len, Math.max(sa, sb)) - Math.max(0, Math.min(sa, sb));
      if (overlap < -tol) return;
      const gap = Math.abs(projN(s, mid(e)));
      if (gap > tol) return;
      const score = gap + Math.max(0, -overlap);
      if (!best || score < best.score) best = { e, s, score };
    }),
  );
  return best;
};

// Snap a proposed placement: align the angle to a facing side, close the gap, line up
// the ends, else corner-to-corner, else the 1" grid. Returns { x, y, rot, connected }.
export const snapPlacement = (piece, others, mode, opts = {}) => {
  const out = {
    x: Number(piece.x) || 0,
    y: Number(piece.y) || 0,
    rot: Number(piece.rot) || 0,
    connected: null,
  };
  if (mode === "off") return out;
  const move = opts.translate !== false;
  const tol = SNAP_IN;
  const statics = others.flatMap((o) =>
    worldEdges(o)
      .filter((e) => e.len >= 2)
      .map((e) => ({ ...e, pid: o.id })),
  );
  const pair = facingPair(
    worldEdges({ ...piece, ...out }).filter((e) => e.len >= 2),
    statics,
    tol,
  );
  if (pair) {
    const want = deg(Math.atan2(-pair.s.nz, -pair.s.nx));
    const have = deg(Math.atan2(pair.e.nz, pair.e.nx));
    out.rot = norm360(out.rot + norm180(want - have));
    if (move) {
      const e = worldEdges({ ...piece, ...out })[pair.e.i];
      const s = pair.s;
      const g = projN(s, mid(e));
      out.x += g * e.nx;
      out.y += g * e.nz;
      const ta = projT(s, e.A);
      const tb = projT(s, e.B);
      const d = [-ta, -tb, s.len - ta, s.len - tb].reduce(
        (b, c) => (Math.abs(c) < Math.abs(b) ? c : b),
        Infinity,
      );
      if (Math.abs(d) <= tol) {
        out.x += d * s.tx;
        out.y += d * s.tz;
      }
    }
    out.connected = pair.s.pid;
  } else if (move) {
    const mine = (piece.points || []).map((pt) =>
      planToWorld({ ...piece, ...out }, pt),
    );
    let best = null;
    others.forEach((o) =>
      (o.points || []).forEach((pt) => {
        const P = planToWorld(o, pt);
        mine.forEach((M) => {
          const d = Math.hypot(P[0] - M[0], P[1] - M[1]);
          if (d <= tol && (!best || d < best.d))
            best = { d, dx: P[0] - M[0], dz: P[1] - M[1], pid: o.id };
        });
      }),
    );
    if (best) {
      out.x += best.dx;
      out.y += best.dz;
      out.connected = best.pid;
    }
  }
  if (mode === "grid" && move && !out.connected) {
    out.x = Math.round(out.x / GRID_IN) * GRID_IN;
    out.y = Math.round(out.y / GRID_IN) * GRID_IN;
  }
  out.x = round2(out.x);
  out.y = round2(out.y);
  out.rot = round2(out.rot);
  return out;
};

const SEAM_GAP = 0.5;
const SEAM_MIN = 1;

// 24.5 -> 24 1/2″ (nearest sixteenth) for seam / joint labels.
export const fmtIn = (v) => {
  const n = Math.round((Number(v) || 0) * 16);
  const whole = Math.floor(n / 16);
  let num = n % 16;
  let den = 16;
  while (num && num % 2 === 0) {
    num /= 2;
    den /= 2;
  }
  if (!num) return `${whole}″`;
  return whole ? `${whole} ${num}/${den}″` : `${num}/${den}″`;
};

// Every joint where two flat tops meet edge-to-edge (facing sides, no gap, same height):
// the shared run in world space, its length and the two pieces. Each pair is visited once.
export const pieceSeams = (flats) => {
  const list = flats.filter(
    (p) => p.orientation !== "vertical" && (p.points || []).length >= 3,
  );
  const edgesOf = list.map((p) => worldEdges(p).filter((e) => e.len >= SEAM_MIN));
  const cosTol = -Math.cos(rad(ANG_TOL));
  const out = [];
  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      const la = Number(list[i].lift) || 0;
      const lb = Number(list[j].lift) || 0;
      if (Math.abs(la - lb) > SEAM_GAP) continue;
      edgesOf[i].forEach((e) =>
        edgesOf[j].forEach((s) => {
          if (e.nx * s.nx + e.nz * s.nz > cosTol) return;
          if (Math.abs(projN(s, mid(e))) > SEAM_GAP) return;
          const ta = projT(s, e.A);
          const tb = projT(s, e.B);
          const lo = Math.max(0, Math.min(ta, tb));
          const hi = Math.min(s.len, Math.max(ta, tb));
          if (hi - lo < SEAM_MIN) return;
          out.push({
            id: `${list[i].id}:${e.i}-${list[j].id}:${s.i}`,
            a: list[i].id,
            b: list[j].id,
            edge_a: e.i,
            edge_b: s.i,
            from: [round2(s.A[0] + lo * s.tx), round2(s.A[1] + lo * s.tz)],
            to: [round2(s.A[0] + hi * s.tx), round2(s.A[1] + hi * s.tz)],
            len: round2(hi - lo),
            lift: Math.max(la, lb),
          });
        }),
      );
    }
  }
  return out;
};

const segDist = ([px, pz], A, B) => {
  const dx = B[0] - A[0];
  const dz = B[1] - A[1];
  const L2 = dx * dx + dz * dz || 1;
  const t = Math.max(0, Math.min(1, ((px - A[0]) * dx + (pz - A[1]) * dz) / L2));
  return Math.hypot(px - A[0] - t * dx, pz - A[1] - t * dz);
};

// Counter side closest to a floor point — where a dragged upright panel will attach.
export const nearestEdge = (P, flats, maxDist = 12) => {
  let best = null;
  flats.forEach((f) =>
    worldEdges(f).forEach((e) => {
      const d = segDist(P, e.A, e.B);
      if (d <= maxDist && (!best || d < best.d))
        best = { d, piece_id: f.id, edge: e.i };
    }),
  );
  return best;
};

// Mirroring reverses the ring, so side k of the mirrored piece is old side n-2-k.
export const remapEdge = (i, n) => (((n - 2 - (Number(i) || 0)) % n) + n) % n;

const swapLR = (e) =>
  e.appliance_facing === "left"
    ? { ...e, appliance_facing: "right" }
    : e.appliance_facing === "right"
      ? { ...e, appliance_facing: "left" }
      : e;

export const mirrorPiece = (p, axis) => {
  const pts = p.points || [];
  const n = pts.length;
  if (n < 3) return p;
  const xs = pts.map((a) => a[0]);
  const ys = pts.map((a) => a[1]);
  const x0 = Math.min(...xs);
  const x1 = Math.max(...xs);
  const y0 = Math.min(...ys);
  const y1 = Math.max(...ys);
  const m =
    axis === "h"
      ? ([x, y]) => [round2(x0 + x1 - x), y]
      : ([x, y]) => [x, round2(y0 + y1 - y)];
  const points = pts.map(m).reverse();
  const edges = pts.map((_, k) => swapLR((p.edges || [])[remapEdge(k, n)] || {}));
  const cutouts = (p.cutouts || []).map((c) => {
    const w = Number(c.w) || 0;
    const h = Number(c.h) || 0;
    const rot = -(Number(c.rot) || 0);
    return axis === "h"
      ? { ...c, x: round2(x0 + x1 - (Number(c.x) || 0) - w), rot }
      : { ...c, y: round2(y0 + y1 - (Number(c.y) || 0) - h), rot };
  });
  return { ...p, points, edges, cutouts };
};

const mirrorCache = new WeakMap();
const flipped = (p, h, v) => {
  const key = `${h ? 1 : 0}${v ? 1 : 0}`;
  const hit = mirrorCache.get(p);
  if (hit?.key === key) return hit.val;
  let q = p;
  if (h) q = mirrorPiece(q, "h");
  if (v) q = mirrorPiece(q, "v");
  mirrorCache.set(p, { key, val: q });
  return q;
};

// Layer the 3D arrangement over the drawn pieces (and re-point derived panels).
export const arrangedPieces = (pieces, panels, over) => {
  if (!over || !Object.keys(over).length) return { pieces, panels };
  const out = pieces.map((p) => {
    const o = over[p.id];
    if (!o) return p;
    const q = { ...flipped(p, !!o.flip_h, !!o.flip_v) };
    if (o.x != null) q.x = o.x;
    if (o.y != null) q.y = o.y;
    if (o.rot != null) q.rot = o.rot;
    if (o.lift != null) q.lift = o.lift;
    if ("attach" in o) q.attach = o.attach || {};
    if (o.stand) q.stand = o.stand;
    return q;
  });
  const byId = Object.fromEntries(pieces.map((p) => [p.id, p]));
  const pans = panels.map((pn) => {
    const o = over[pn.panel_of];
    const parent = byId[pn.panel_of];
    if (!o || !parent || (!o.flip_h && !o.flip_v)) return pn;
    const n = (parent.points || []).length;
    let e = Number(pn.panel_edge) || 0;
    if (o.flip_h) e = remapEdge(e, n);
    if (o.flip_v) e = remapEdge(e, n);
    return { ...pn, panel_edge: e };
  });
  return { pieces: out, panels: pans };
};

// Write the arranged pieces back into the area's piece list (only the ones that moved).
export const toDrawingPieces = (areaPieces, arranged, panels, over) =>
  areaPieces.map((p) => {
    if (p.panel_kind) {
      if (!over[p.panel_of]) return p;
      const src = panels.find((q) => q.id === p.id);
      return src ? { ...p, panel_edge: src.panel_edge } : p;
    }
    if (!over[p.id]) return p;
    const src = arranged.find((q) => q.id === p.id);
    if (!src) return p;
    return {
      ...p,
      x: src.x,
      y: src.y,
      rot: src.rot,
      lift: Number(src.lift) || 0,
      points: src.points,
      edges: src.edges,
      cutouts: src.cutouts,
      attach: src.attach || {},
      stand: src.stand || p.stand,
    };
  });

const SEAM_CUTOUT_CLEAR = 3;

// Usable slab field for a quote/area: layout slabs, else matching inventory, else the shop default.
export const usableSlab = (quote, area, slabStock = [], general = {}) => {
  const trim = 2;
  const color = (area?.slab_color || quote?.slab_color || "").trim().toLowerCase();
  const sized = (s) => Number(s.length_in) > 0 && Number(s.width_in) > 0;
  const layout = ((quote?.layout || {}).slabs || []).filter(sized);
  const stock = slabStock.filter(
    (s) => sized(s) && (s.slab_color || "").trim().toLowerCase() === color,
  );
  const pick = layout.length ? layout : stock;
  const L = pick.length
    ? Math.min(...pick.map((s) => Number(s.length_in)))
    : Number(general.slab_length_in) || 126;
  const W = pick.length
    ? Math.min(...pick.map((s) => Number(s.width_in)))
    : Number(general.slab_width_in) || 63;
  return {
    usable_l: L - trim,
    usable_w: W - trim,
    source: layout.length ? "layout" : stock.length ? "inventory" : "default",
  };
};

const localBox = (p) => {
  const xs = (p.points || []).map((q) => q[0]);
  const ys = (p.points || []).map((q) => q[1]);
  return [Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)];
};

// Why a seam should be looked at: too close to / over a cutout, longer than the slab, or a
// piece on either side that cannot come out of one slab.
export const seamIssues = (seam, byId, slab) => {
  const issues = [];
  const { usable_l: UL, usable_w: UW } = slab;
  if (seam.len > UL) issues.push(`Seam ${fmtIn(seam.len)} is longer than the ${UL}″ slab`);
  [seam.a, seam.b].forEach((id) => {
    const p = byId[id];
    if (!p) return;
    const name = p.label || "Piece";
    const [l, w] = localBox(p);
    const fits = (l <= UL && w <= UW) || (w <= UL && l <= UW);
    if (!fits) issues.push(`${name} is ${fmtIn(l)} × ${fmtIn(w)} — over the ${UL}″ × ${UW}″ slab, needs another seam`);
    (p.cutouts || []).forEach((c) => {
      const ring = cutoutRing(c).map((pt) => planToWorld(p, pt));
      const d = Math.min(...ring.map((pt) => segDist(pt, seam.from, seam.to)));
      if (d < SEAM_CUTOUT_CLEAR) {
        const what = c.kind || c.label || "cutout";
        issues.push(
          d < 0.25
            ? `Seam runs over the ${what} on ${name}`
            : `Seam is ${fmtIn(d)} from the ${what} on ${name} (keep ${SEAM_CUTOUT_CLEAR}″)`,
        );
      }
    });
  });
  return issues;
};

// Seams for the whole quote as saved (draw-tab pieces + 3D arrangement) — stored in
// quote.design.seams so the sign-off drawing and install packet can list them.
export const quoteSeams = (quote) => {
  const arrange = quote?.design?.arrange || {};
  const out = [];
  (quote?.areas || []).forEach((a) => {
    const flats = (a.pieces || []).filter(
      (p) => !p.panel_kind && (p.points || []).length >= 3,
    );
    const { pieces } = arrangedPieces(flats, [], arrange[a.id] || {});
    const byId = Object.fromEntries(pieces.map((p, i) => [p.id, p.label || `Piece ${i + 1}`]));
    pieceSeams(pieces).forEach((s) =>
      out.push({
        area: a.name || "Area",
        area_id: a.id,
        a: s.a,
        b: s.b,
        label: `${byId[s.a]} ↔ ${byId[s.b]}`,
        len: s.len,
        len_text: fmtIn(s.len),
      }),
    );
  });
  return out;
};
